import type { Cart, ProductDeviceInCart, ProductVariant } from '@telialt/models/src/EshopModel';

import type { GTMEventName } from '../../types';
import type { OTTPlanCheckoutInput, TVOrderingCheckoutInput } from '../tv/types';

export const HUDSON_CHECKOUT_EVENTS = {
    acceptTermsAndConditions: 'accept_terms_and_conditions',
    addClientInfo: 'add_client_info',
    addPaymentInfo: 'add_payment_info',
    addShippingInfo: 'add_shipping_info',
    beginCheckout: 'begin_checkout',
    cancelPayment: 'cancel_payment',
    cancelSignDocument: 'cancel_sign_document',
    confirmPayment: 'confirm_payment',
    purchase: 'purchase',
    signDocument: 'sign_document',
    successfulCreditCheck: 'successful_credit_check',
    unsuccessfulCreditCheck: 'unsuccessful_credit_check',
} as const satisfies Record<string, GTMEventName>;

export const HYBRIS_CHECKOUT_EVENTS = {
    addClientInfo: 'add_client_info',
    addPaymentInfo: 'add_payment_info',
    addShippingInfo: 'add_shipping_info',
    beginCheckout: 'begin_checkout',
    documentSign: 'document_sign',
    purchase: 'purchase_event',
} as const satisfies Record<string, GTMEventName>;

export type HudsonCheckoutPrice = {
    value?: number;
};

export type HudsonCheckoutCharacteristic = {
    name: string;
    valueType: string;
    value?: string | number | boolean;
};

export type HudsonCheckoutCartEntry = {
    product: {
        id: string;
        name: string;
        variantOption?: string;
    };
    authorsFee?: HudsonCheckoutPrice;
    totals?: {
        recurring?: HudsonCheckoutPrice;
    };
    ga4Prices?: {
        totalPrice?: HudsonCheckoutPrice;
        discount?: HudsonCheckoutPrice;
    };
    characteristics?: HudsonCheckoutCharacteristic[];
    realizingResource?: {
        id: string;
        value: string;
    };
};

export type HudsonCheckoutCart = {
    customer: {
        email: string;
    };
    paymentMethod?: {
        type?: string;
        bank?: {
            name?: string;
        };
    };
    totals?: {
        recurring?: HudsonCheckoutPrice;
    };
    entries: HudsonCheckoutCartEntry[];
};

export type HudsonCheckoutAction = keyof typeof HUDSON_CHECKOUT_EVENTS;

export type HudsonCheckoutEventName = (typeof HUDSON_CHECKOUT_EVENTS)[HudsonCheckoutAction];

export type HudsonCheckoutEventData = {
    action: HudsonCheckoutAction;
    cart: HudsonCheckoutCart;
    userId: string;
    segment?: string;
    product?: ProductDeviceInCart | ProductVariant;
    giftProduct?: ProductDeviceInCart | ProductVariant;
    ecommerce?: {
        paymentMethod?: string;
        signMethod?: string;
        bankName?: string;
        transactionId?: string;
    };
};

export type HybrisCheckoutAction = keyof typeof HYBRIS_CHECKOUT_EVENTS;

export type HybrisCheckoutEventName = (typeof HYBRIS_CHECKOUT_EVENTS)[HybrisCheckoutAction];

export type HybrisCheckoutEventData = {
    action: HybrisCheckoutAction;
    cart: Cart;
    segment?: string;
    transactionId?: string;
    paymentType?: string;
};

export type CheckoutInput =
    | {
          source: 'hudsonCheckout';
          data: HudsonCheckoutEventData;
      }
    | {
          source: 'hybrisCheckout';
          data: HybrisCheckoutEventData;
      }
    | OTTPlanCheckoutInput
    | TVOrderingCheckoutInput;
